import styled from "@emotion/styled";
import { useBloc as useBlocNext } from "@blac/react";
import type { FC } from "react";
import React, { useEffect, useRef } from "react";
import { Link as RouterLink } from "react-router-dom";
import Path from "src/constants/path";

import {
  IconBellRingNotifications,
  IconCloudDataSync,
  IconCreditCardPaymentBilling,
  IconGlobe,
  IconInsuranceHandShieldProtection,
  IconLogOutExitCLoseLeaveArrow,
  IconPersonPhysicianProfileStethoscope,
  IconPharmacyMedicalCupSnakeBowlOfHygieia,
  IconProfilePersonAvatarPlaceholderHeadBody,
  IconShippingPackageBox,
  InfoCircleIcon
} from "src/constants/icons";
import AppVersionBloc from "src/hybrid/AppVersionBloc";
import { OpenBrowser } from "src/hybrid/components/Browser";
import translate from "src/lib/translate";
import AuthenticationBloc from "src/state/UserCubit/AuthenticationBloc";
import UserCubit, {
  ProfileAttributesKeys
} from "src/state/UserCubit/UserCubit";
import UserPreferencesCubit from "src/state/UserPreferencesCubit/UserPreferencesCubit";
import { useBloc, userState } from "src/state/state";
import Avatar from "src/ui/components/Avatar/Avatar";
import Collection from "src/ui/components/StyledComponents/Collection";
import CollectionItemLink from "src/ui/components/StyledComponents/CollectionItemLink";
import Translate from "../Translate/Translate";
import HealthSyncBloc from "src/state/HealthSyncBloc/HealthSyncBloc";
import AppDebugCubit from "../AppDebug/AppDebugCubit";
import AppViewCubit from "src/state/AppViewCubit/AppViewCubit";
import { getHeadingProps, useFocusHeading } from "src/lib/useFocusHeading";
import ConsistencyScoreTile from "../ConsistencyScoreTile/ConsistencyScoreTile";
import { dateLocal } from "src/lib/date";
import profileImage from "src/ui/assets/food-and-medication.webp";
import { APP_BREAKPOINT } from "src/constants/layout";

export const PROFILE_URL = "/app/profile";

const PageWrap = styled.div`
  padding-bottom: 5rem;
  @media (max-width: ${APP_BREAKPOINT - 1}px) {
    padding: 0 1.6rem;
  }
`;

const DesktopOnly = styled.div`
  @media (max-width: ${APP_BREAKPOINT - 1}px) {
    display: none;
  }
`;

const Header = styled.div`
  position: relative;
  padding: 0;
  overflow: hidden;
  border-radius: 0.5rem;
  aspect-ratio: 375 / 200;
  background: var(--color-charcoal-12);

  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  @media (max-width: ${APP_BREAKPOINT - 1}px) {
    border-radius: 0;
    margin: 0;
  }
`;

const StickyCloseContainer = styled.div`
  display: none;

  @media (max-width: ${APP_BREAKPOINT - 1}px) {
    display: block;
    position: sticky;
    top: 0;
    height: 0;
    overflow: visible;
    z-index: 20;
  }
`;

const CloseButton = styled.button`
  display: none;
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  background: none;
  border: none;
  padding: 0.375rem;
  cursor: pointer;
  min-width: 32px;
  min-height: 32px;
  align-items: center;
  justify-content: center;

  @media (max-width: ${APP_BREAKPOINT - 1}px) {
    display: flex;
    background: var(--color-charcoal-12);
    border-radius: 50%;
    &:hover {
      background: var(--color-charcoal-22);
    }
  }
`;

const HeaderOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  padding: 1.6rem;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.45) 0%, transparent 100%);
  color: white;

  .content {
    margin-left: 1rem;
    max-width: calc(100% - 4rem);
  }
`;

const Name = styled.h4`
  color: var(--Greys-White, #fff);
  font-feature-settings:
    "liga" off,
    "clig" off;

  font-family: "STK Bureau Serif", Serif;
  font-size: 28px;
  font-weight: 300;
  line-height: 120%;
  letter-spacing: -0.56px;

  @media (min-width: ${APP_BREAKPOINT}) {
    font-size: 40px;
    letter-spacing: -1px;
  }
`;

const RegisteredAt = styled.p`
  color: var(--Greys-White, #fff);
  font-feature-settings:
    "liga" off,
    "clig" off;
  font-family: "STK Bureau";
  font-size: 14px;
  font-weight: 400;
  line-height: 140%;
  letter-spacing: -0.28px;

  @media (min-width: ${APP_BREAKPOINT}) {
    font-size: 16px;
    letter-spacing: -0.32px;
  }
`;

export enum ProfileMenuItem {
  account = "/account",
  billing = "/billing",
  dataSync = "/data-sync",
  dataSyncLearnMore = "/data-sync-learn-more",
  notifications = "/notifications",
  shipping = "/shipping",
  insurance = "/insurance",
  pharmacy = "/pharmacy",
  pcp = "/primary-care-physician",
  language = "/language"
}

interface ProfileOverviewProps {
  onClose?: () => void;
}

const ProfileOverview: FC<ProfileOverviewProps> = ({ onClose }) => {
  const [, { displayName, loadUserPreferences }] =
    useBlocNext(UserPreferencesCubit);
  const [{ userData }] = useBloc(UserCubit);
  const [, { logout }] = useBlocNext(AuthenticationBloc);
  const [, { currentVersionString }] = useBloc(AppVersionBloc);
  const [{ available }] = useBloc(HealthSyncBloc);
  const [{ partnerSession }] = useBlocNext(AppViewCubit);
  const [, appDebugCubit] = useBlocNext(AppDebugCubit);
  const { headingRef } = useFocusHeading();
  const tapCountRef = useRef(0);
  const { profileAttributes } = userState.state;
  const registeredAtValue =
    profileAttributes?.[ProfileAttributesKeys.registeredAt];
  const tapTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleVersionTap = () => {
    tapCountRef.current += 1;
    if (tapTimeoutRef.current) {
      clearTimeout(tapTimeoutRef.current);
    }
    if (tapCountRef.current >= 8) {
      tapCountRef.current = 0;
      appDebugCubit.toggleOrReset();
    } else {
      tapTimeoutRef.current = setTimeout(() => {
        tapCountRef.current = 0;
      }, 1000);
    }
  };

  useEffect(() => {
    void loadUserPreferences();
  }, []);

  const openHelpCenter = () => {
    const url = "https://help.9am.health/hc/en-us";
    void OpenBrowser(url, {
      useBaseUrl: false,
      presentationStyle: "popover"
    });
  };

  const handleClick = async () => {
    await logout();
  };

  return (
    <>
      <h1 className="only-sr" ref={headingRef} {...getHeadingProps()}>
        <Translate msg="profilePage" />
      </h1>

      <DesktopOnly>
        <nine-spacer s="sm"></nine-spacer>
      </DesktopOnly>

      {onClose && (
        <StickyCloseContainer>
          <CloseButton onClick={onClose} aria-label="Close">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="32"
              height="32"
              viewBox="0 0 32 32"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M9.33337 9.33334L22.6667 22.6667M9.33337 22.6667L22.6667 9.33334"
                stroke="white"
                strokeWidth="1.33333"
                strokeLinecap="round"
              />
            </svg>
          </CloseButton>
        </StickyCloseContainer>
      )}

      <Header>
        <img src={profileImage} alt="" aria-hidden="true" />
        <HeaderOverlay>
          <Avatar border edit id={userData?.id} memberAvatar />
          <div className="content">
            <Name className="m0">{displayName}</Name>
            <RegisteredAt className="as-body m0">
              <Translate
                msg="profile.registeredAt"
                variables={{
                  date: dateLocal(registeredAtValue).format("MMMM D, YYYY")
                }}
              />
            </RegisteredAt>
          </div>
        </HeaderOverlay>
      </Header>

      <PageWrap>
        <nine-spacer s="lg"></nine-spacer>

        <ConsistencyScoreTile />
        <Collection className="mt-1-5">
          <CollectionItemLink
            icon={<IconProfilePersonAvatarPlaceholderHeadBody />}
            label={translate("accountInformation")}
            to={PROFILE_URL + ProfileMenuItem.account}
          />
          {!partnerSession && (
            <CollectionItemLink
              icon={<IconCreditCardPaymentBilling />}
              label={translate("billingAndPayment")}
              to={PROFILE_URL + ProfileMenuItem.billing}
            />
          )}
          {available && (
            <CollectionItemLink
              icon={<IconCloudDataSync />}
              label={translate("dataSync")}
              to={PROFILE_URL + ProfileMenuItem.dataSync}
            />
          )}
          {!partnerSession && (
            <>
              <CollectionItemLink
                icon={<IconGlobe />}
                label={translate("language")}
                to={PROFILE_URL + ProfileMenuItem.language}
              />
              <CollectionItemLink
                icon={<IconBellRingNotifications />}
                label={translate("notifications")}
                to={PROFILE_URL + ProfileMenuItem.notifications}
              />
              <CollectionItemLink
                icon={<IconShippingPackageBox />}
                label={translate("shippingInformation")}
                to={PROFILE_URL + ProfileMenuItem.shipping}
              />
            </>
          )}
        </Collection>

        <Collection className="mt-1-5">
          <CollectionItemLink
            icon={<IconInsuranceHandShieldProtection />}
            label={translate("insuranceInformation")}
            to={PROFILE_URL + ProfileMenuItem.insurance}
          />

          <CollectionItemLink
            icon={<IconPersonPhysicianProfileStethoscope />}
            label={translate("primaryCarePhysician")}
            to={PROFILE_URL + ProfileMenuItem.pcp}
          />

          <CollectionItemLink
            icon={<IconPharmacyMedicalCupSnakeBowlOfHygieia />}
            label={translate("yourPharmacy")}
            to={PROFILE_URL + ProfileMenuItem.pharmacy}
          />
        </Collection>

        {!partnerSession && (
          <Collection className="mt-1-5">
            <CollectionItemLink
              icon={<InfoCircleIcon />}
              label={translate("helpCenter")}
              onClick={openHelpCenter}
            />
          </Collection>
        )}
        {!partnerSession && (
          <Collection className="mt-1-5">
            <CollectionItemLink
              icon={<IconLogOutExitCLoseLeaveArrow />}
              label={translate("logout")}
              onClick={() => void handleClick()}
            />
          </Collection>
        )}

        <nine-spacer s="sm"></nine-spacer>
        <div
          className="as-little color-c-60"
          onClick={handleVersionTap}
          style={{ cursor: "default", userSelect: "none" }}
        >
          <Translate
            msg="version"
            variables={{ version: currentVersionString }}
          />
        </div>
        <nine-spacer s="lg"></nine-spacer>
        <div className="as-little color-c-60">
          <RouterLink
            to={`/${Path.openSourceLicenses}`}
            style={{ color: "inherit", textDecoration: "underline" }}
          >
            <Translate msg="openSourceLicenses" />
          </RouterLink>
        </div>
      </PageWrap>
    </>
  );
};

export default ProfileOverview;
